/**
 * Formata um valor numérico para o padrão de moeda brasileira (R$)
 * @param {number} value - Valor a ser formatado
 * @returns {string} Valor formatado como moeda brasileira
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'R$ 0,00';
  }
  
  // Garante que o valor é um número
  const number = typeof value === 'string' ? parseFloat(value) : value;
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

/**
 * Formata uma data para o formato brasileiro (DD/MM/YYYY)
 * @param {string|Date} date - Data a ser formatada (pode ser string ou objeto Date)
 * @returns {string} Data formatada como string no formato DD/MM/YYYY
 */
export const formatDate = (date) => {
  if (!date) return '--/--/----';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Verifica se a data é válida
    if (isNaN(dateObj.getTime())) {
      return '--/--/----';
    }
    
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '--/--/----';
  }
};

/**
 * Formata um CPF (###.###.###-##)
 * @param {string} cpf - CPF a ser formatado
 * @returns {string} CPF formatado
 */
export const formatCPF = (cpf) => {
  if (!cpf) return '';
  
  // Remove todos os caracteres não numéricos
  const cleaned = cpf.replace(/\D/g, '');
  
  // Aplica a formatação
  return cleaned
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

/**
 * Formata um telefone no formato (##) #####-####
 * @param {string} phone - Telefone a ser formatado
 * @returns {string} Telefone formatado
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  
  // Remove todos os caracteres não numéricos
  const cleaned = phone.replace(/\D/g, '');
  
  // Verifica se é celular (com 9º dígito) ou telefone fixo
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  } else {
    // Retorna o valor original se não for um telefone válido
    return phone;
  }
};

/**
 * Formata um CEP (#####-###)
 * @param {string} cep - CEP a ser formatado
 * @returns {string} CEP formatado
 */
export const formatCEP = (cep) => {
  if (!cep) return '';
  
  // Remove todos os caracteres não numéricos
  const cleaned = cep.replace(/\D/g, '');
  
  // Aplica a formatação
  return cleaned.replace(/^(\d{5})(\d{3})$/, '$1-$2');
};

/**
 * Formata um valor percentual (##,##%)
 * @param {number} value - Valor a ser formatado (ex: 0.1 para 10%)
 * @param {number} decimals - Número de casas decimais (padrão: 2)
 * @returns {string} Valor formatado como percentual
 */
export const formatPercent = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0,00%';
  }
  
  const number = typeof value === 'string' ? parseFloat(value) : value;
  const multiplier = Math.pow(10, decimals);
  const rounded = Math.round(number * 100 * multiplier) / multiplier;
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(rounded / 100);
};

/**
 * Formata um número com separadores de milhar e casas decimais
 * @param {number} value - Valor a ser formatado
 * @param {number} decimals - Número de casas decimais (padrão: 2)
 * @returns {string} Número formatado
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0,00';
  }
  
  const number = typeof value === 'string' ? parseFloat(value) : value;
  
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(number);
};

export default {
  formatCurrency,
  formatDate,
  formatCPF,
  formatPhone,
  formatCEP,
  formatPercent,
  formatNumber
};
